import React, { useMemo } from 'react';
import { Button, Col, Row, Typography } from 'antd';
import {
  ArrowLeftOutlined,
  CompassOutlined,
  HomeOutlined,
  LockOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import LottieFile from 'assets/lottie-files';
import { getWhatsAppUrl } from 'helpers';
import { useI18n } from 'hooks/useI18n';
import LottieComponent from './lottie-component';
import './no-access.scss';

const { Paragraph, Title } = Typography;

const SUPPORT_WHATSAPP_NUMBER = '7904557013';

const NoAccess = ({ featureName = '', variant = 'forbidden' }) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();

  const isMissingRoute = variant === 'missing';
  const isSubscriptionRequired = variant === 'subscription';

  const subjectSuffix = featureName || t('no_access.default_feature');
  const title = isMissingRoute
    ? t('no_access.missing_title')
    : isSubscriptionRequired
    ? t('no_access.subscription_title')
    : t('no_access.title');
  const eyebrowLabel = isMissingRoute
    ? t('no_access.missing_eyebrow')
    : isSubscriptionRequired
    ? t('no_access.subscription_eyebrow')
    : t('no_access.forbidden_eyebrow');
  const description = isMissingRoute
    ? t('no_access.missing_description', { route: location.pathname })
    : isSubscriptionRequired
    ? t('no_access.subscription_description')
    : featureName
    ? t('no_access.feature_description', { feature: featureName })
    : t('no_access.description');
  const contactAdminHref = useMemo(() => {
    const message = t('no_access.whatsapp_message', {
      feature: subjectSuffix,
      route: location.pathname,
    });

    return getWhatsAppUrl(SUPPORT_WHATSAPP_NUMBER, message);
  }, [location.pathname, subjectSuffix, t]);

  return (
    <div className={`no-access-page ${variant}`}> {/* Updated className to use variant directly */}
      <div className="no-access-shell">
        <Row gutter={[24, 24]} className="no-access-hero">
          <Col xs={24} lg={14} className="no-access-copy">
            <div className="no-access-copy__eyebrow">
              {isMissingRoute ? <CompassOutlined /> : <LockOutlined />}
              <span>{eyebrowLabel}</span>
            </div>

            <Title level={1} className="no-access-copy__title">
              {title}
            </Title>

            <Paragraph className="no-access-copy__description">{description}</Paragraph>

            <div className="no-access-actions">
              {isMissingRoute ? (
                <>
                  <Button
                    type="primary"
                    className="no-access-actions__primary"
                    onClick={() => navigate('/')}
                    icon={<HomeOutlined />}
                  >
                    {t('no_access.back_to_dashboard')}
                  </Button>
                  <Button
                    className="no-access-actions__secondary"
                    onClick={() => navigate(-1)}
                    icon={<ArrowLeftOutlined />}
                  >
                    {t('no_access.go_back')}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    type="primary"
                    href={contactAdminHref}
                    icon={<MessageOutlined />}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('no_access.contact_admin')}
                  </Button>
                  {isSubscriptionRequired && (
                    <Button
                      className="no-access-actions__secondary"
                      onClick={() => navigate('/')}
                      icon={<HomeOutlined />}
                    >
                      {t('no_access.back_to_dashboard')}
                    </Button>
                  )}
                </>
              )}
            </div>
          </Col>

          <Col xs={24} lg={10} className="no-access-visual">
            <div className="no-access-visual__badge">
              {isMissingRoute ? <CompassOutlined /> : <LockOutlined />}
              <span>{eyebrowLabel}</span>
            </div>
            <div className="no-access-visual__animation">
              <LottieComponent width={'100%'} file={LottieFile.UnderConstruction} />
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default NoAccess;
